import moment from 'moment';
import { useContext, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BiPlus, BiSolidFileDoc, BiSolidFileHtml, BiSolidFileJson, BiSolidFileMd, BiSolidFilePdf, BiSolidFileTxt, BiSolidTrash } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import {
  useAddKbAssistantBotMutation,
  useDeleteFileKbAssistantBotMutation,
  useGenerateUploadUrlMutation,
  useValidateUploadedItemMutation,
} from '@/app/services/chatbot-rtk.services';
import { BannerHelper } from '@/components/common/BannerToast';
import { PSButton } from '@/components/ui/button';
import { PSInput } from '@/components/ui/input';
import { AlertsType } from '@/constants/alerts.constant';
import { ChatbotEditContext } from '@/pages/dashboard/ChatbotEdit';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import { uploadFileWithPolicy } from '@/utils/cdnUpload';
import { resolveKbFileSizeLimit, selectKnowledgeBasePermission, toMegabytes } from '@/utils/uploadLimits';

import LoadingComponent from '../../components/common/loading/loading.component';
import PopoverWithAudio, { IPopoverWithAudioType } from '../../components/common/PopoverWithAudio';
import { selectUserProfile } from '../auth/auth.slice';
import { handleRemoveTargetKnowledgeFile, handleResetWizard, handleSetAiKnowledgeFiles, wizardState } from './botSlice';
import KnowledgeBaseAlert from './KnowledgeBaseAlert';
import { AIKittyType, CdnFileType } from './types';

// Define TypeScript interfaces

function instanceOfCdnFile(object: any): object is CdnFileType {
  return 'cdn_link' in object;
}

const KnowledgeSeederEdit = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const ChatbotEditContextValue = useContext(ChatbotEditContext);
  const wizard = useAppSelector(wizardState);
  const permissions = selectKnowledgeBasePermission(useAppSelector(state => (state.dashboardState as any).Client.botConfigs));

  const profile = useAppSelector(selectUserProfile);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const maxFileSize = resolveKbFileSizeLimit(permissions);

  const fileTypes = [
    'application/pdf', // pdf
    'text/plain', // txt
    'text/markdown', // md
    'application/json', // json
  ];

  const [addFileKbAiKittyBot] = useAddKbAssistantBotMutation({
    fixedCacheKey: 'aiKnowledgeFileMutator',
  });
  const [removeFileKbAiKittyBot, removeFileKbAiKittyBotResults] = useDeleteFileKbAssistantBotMutation({
    fixedCacheKey: 'aiKnowledgeFileDeleteMutator',
  });
  const [GenerateUploadUrlRequest, GenerateUploadUrlRequestStatus] = useGenerateUploadUrlMutation();
  const [uploadVerify, uploadVerifyResults] = useValidateUploadedItemMutation();

  const [uiFiles, setUiFiles] = useState<File[] | AIKittyType['files'] | []>([]);

  useEffect(() => {
    if ((wizard.bot_config as AIKittyType)?.files) {
      setUiFiles((wizard.bot_config as AIKittyType)?.files);
    }
  }, [(wizard.bot_config as AIKittyType)?.files]);

  const [invalidUiFiles, setInvalidUiFiles] = useState<string[] | []>([]);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);

  const handleGenerateUploadUrl = async (file: any, fileName: string, isPrivate: boolean) => {
    try {
      const response = await GenerateUploadUrlRequest({
        fileName: (profile?.user_identification ? profile.user_identification : '') + '_' + fileName,
        isPrivate: isPrivate,
      })
        .unwrap()
        .catch((err: any) => {
          throw new Error(err?.data?.message || err?.message || 'Error on generating private link, please contact the support!');
        });

      const responseUrl = response;

      if (!responseUrl?.file_name || !responseUrl?.cdn_pat) {
        throw new Error('Invalid upload response, please contact the support!');
      }

      await uploadFileWithPolicy(responseUrl, file);

      await uploadVerify({ file_name: responseUrl.file_name }).unwrap();

      return responseUrl;
    } catch (err: any) {
      throw new Error(err?.data?.message || err?.message || 'Upload process failure.');
    }
  };

  const handleValidateAndAddFile = (files: FileList | null) => {
    setInvalidUiFiles([]);

    const invalidFilesNames: string[] = [];
    const validFiles: File[] = [];

    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i] as File;

        if (file.size < 1024) {
          toast.error(t('KbAssistantUploadFilesComponent/fileSizeLimitError'));
          return;
        }

        if (!fileTypes.includes(file.type)) {
          toast.error(t('KbAssistantUploadFilesComponent/text-05'));

          return invalidFilesNames.push(file.name);
        }

        if (file.size > maxFileSize) {
          toast.error(
            t('KbAssistantUploadFilesComponent/text-04', {
              size: toMegabytes(maxFileSize),
            })
          );

          return invalidFilesNames.push(file.name);
        }

        validFiles.push(file);
      }

      setInvalidUiFiles(invalidFilesNames);

      setIsUploadInProgress(true);

      const currentFile = validFiles[0];
      let acceptedFiles: CdnFileType[] = [];
      const rejectedFiles: string[] = [];

      handleGenerateUploadUrl(currentFile, currentFile.name, true)
        .then(res => {
          addFileKbAiKittyBot({
            data: {
              bot_code: wizard.bot_code,
              cdn_link: res.cdn_pat,
              cdn_name: res.file_name,
              original_name: files[0].name,
              title: wizard.bot_config?.title,
            },
            id: wizard.id,
          })
            .unwrap()
            .then(result => {
              setIsUploadInProgress(false);

              acceptedFiles = [...(result.config.files as CdnFileType[])];

              if (acceptedFiles.length > 0) {
                dispatch(handleSetAiKnowledgeFiles(acceptedFiles));

                setUiFiles(acceptedFiles);

                // setValue("training_data", acceptedFiles);

                if (fileInputRef && fileInputRef.current && fileInputRef.current.value) fileInputRef.current.value = '';
              }
            })
            .catch(err => {
              toast.error(err.data.message || err.message);
              setIsUploadInProgress(false);
            });
        })
        .catch(err => {
          setIsUploadInProgress(false);

          rejectedFiles.push(currentFile.name);

          setInvalidUiFiles(rejectedFiles);

          toast.error(err.data?.message || err.message || err || 'Upload failed.');
        });
    }
  };

  const handleRemoveSelectedFile = (file: File | CdnFileType) => {
    setInvalidUiFiles([]);

    if (!instanceOfCdnFile(file)) {
      setUiFiles([...(uiFiles as File[]).filter(f => f !== file)]);

      if (fileInputRef && fileInputRef.current && fileInputRef.current.value) fileInputRef.current.value = '';
    } else {
      const data = {
        bot_code: wizard.bot_code,
        config: {
          title: wizard.bot_config?.title,
          file,
        },
      };

      removeFileKbAiKittyBot({ data, bot_id: wizard.id })
        .unwrap()
        .then(() => {
          toast.success(t('KbAssistantUploadFilesComponent/t-01'));

          dispatch(handleRemoveTargetKnowledgeFile(file.id));

          const newList = (uiFiles as CdnFileType[])?.filter((f: CdnFileType) => f.id !== (file as CdnFileType).id);

          setUiFiles(newList);
        })
        .catch(res => {
          if (res.status === 500) {
            toast.error(`${t(AlertsType.ISWGKO002)} ${res.data.message}`);
          } else {
            toast.error(t(AlertsType.ISWGKO003));
          }
        });
    }
  };

  const selectFileIcon = (file: any) => {
    let fileName = '';

    if (file.original_name) {
      fileName = file.original_name;
    } else if (file.cdn_name) {
      fileName = file.cdn_name;
    } else {
      fileName = file.name;
    }

    const fileType = fileName?.split('.').pop();
    switch (fileType) {
      case 'pptx':
      case 'xlsx':
      case 'csv':
      case 'xml':
        return <BiSolidFileDoc size={24} className="tw-text-primary" />;
      case 'pdf':
        return <BiSolidFilePdf size={24} className="tw-text-primary" />;
      case 'html':
        return <BiSolidFileHtml size={24} className="tw-text-primary" />;
      case 'json':
        return <BiSolidFileJson size={24} className="tw-text-primary" />;
      case 'md':
        return <BiSolidFileMd size={24} className="tw-text-primary" />;
      case 'txt':
        return <BiSolidFileTxt size={24} className="tw-text-primary" />;
      default:
        return <BiSolidFileDoc size={24} className="tw-text-primary" />;
    }
  };

  const getFileName = (file: any, limitLength: number) => {
    let fileName = '';

    if (file.original_name) {
      fileName = file.original_name;
    } else if (file.cdn_name) {
      const str: string = file.cdn_name;
      fileName = str.substring(str?.lastIndexOf('_') + 1);
      fileName = fileName.replace('_', ' ');
    } else {
      fileName = file.name;
    }

    return fileName?.length > limitLength ? fileName.substring(0, limitLength) + ' ...' : fileName;
  };

  const handleOpenFileSelector = () => {
    if (permissions?.file_limit && uiFiles && uiFiles.length >= permissions.file_limit)
      return toast.error(
        t('KbAssistantUploadFilesComponent/fileCountLimitError', {
          max: permissions.file_limit,
        })
      );

    if (fileInputRef.current) fileInputRef.current.value = '';

    fileInputRef.current?.click();
  };

  const isLoading = isUploadInProgress || removeFileKbAiKittyBotResults.isLoading;

  const LoadingSpinner = () => <Spinner size="sm" className="tw-mr-2" />;

  const IconButton = () => (
    <div className="tw-p-1 tw-bg-primary tw-rounded-[19px] tw-shadow tw-justify-center tw-items-center tw-inline-flex">
      <div className="tw-w-5 tw-h-5 tw-relative tw-flex tw-flex-col tw-items-center tw-justify-center">
        <BiPlus size={22} className="tw-text-white" />
      </div>
    </div>
  );

  const [isLoadingProgress, setIsLoading] = useState(false);

  useEffect(() => {
    if (ChatbotEditContextValue?.value) {
      setIsLoading(ChatbotEditContextValue.value.isLoading);
    } else {
      setIsLoading(true);
    }
  }, [ChatbotEditContextValue?.value]);

  useEffect(() => {
    return () => {
      dispatch(handleResetWizard());
    };
  }, []);

  if (isLoadingProgress) {
    return <LoadingComponent />;
  }

  if (!profile) {
    return <LoadingComponent />;
  }

  return (
    <div className=" tw-p-6 tw-bg-white tw-rounded-2xl tw-shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)] tw-flex-col  ">
      <div className="tw-mb-4">
        <BannerHelper message={t('HelpBanner/Knowledge/hint')} />
      </div>
      <div className="tw-self-stretch tw-flex md:tw-flex-row tw-flex-col md:tw-justify-between md:tw-items-center tw-gap-8 tw-mb-8">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
          <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            {t('KbAssistantUploadFilesComponent/title')}
          </div>
          <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('KbAssistantUploadFilesComponent/st-04')}
          </div>
          <KnowledgeBaseAlert texts={['Wizard/KnowledgeBaseAlert/key1', 'Wizard/KnowledgeBaseAlert/key2', 'Wizard/KnowledgeBaseAlert/key3']} />
          <KnowledgeBaseAlert texts={['Wizard/KnowledgeBaseAlert/key4', 'Wizard/KnowledgeBaseAlert/key5']} />
          <KnowledgeBaseAlert texts={['Wizard/KnowledgeBaseAlert/key7']} />
          <KnowledgeBaseAlert texts={['Wizard/KnowledgeBaseAlert/key8']} />
        </div>
      </div>
      <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-justify-end tw-mb-3  ">
        <PSInput
          type="file"
          hidden
          // multiple
          accept={fileTypes.join(', ').toString()}
          ref={fileInputRef}
          onChange={e => handleValidateAndAddFile(e.target.files)}
        />
        <PSButton id="btn-seeder-open" variant="primary" onClick={handleOpenFileSelector} type="button" disabled={isLoading}>
          {isLoading && <LoadingSpinner />}
          {!isLoading && <IconButton />}
          <div className="tw-p-1 tw-justify-center tw-items-center tw-inline-flex" style={{ whiteSpace: 'nowrap' }}>
            {isLoading ? t('LoadingComponent/text') : t('KbAssistantUploadFilesComponent/btn-02')}
          </div>
        </PSButton>
        <PopoverWithAudio
          data={
            {
              contentText: t('KbAssistantUploadFilesComponent/pop-01').toString(),
            } as IPopoverWithAudioType
          }
        ></PopoverWithAudio>
      </div>
      <div className="tw-flex tw-flex-col">
        {uiFiles?.map((file, index) => (
          <div
            key={index}
            className="md:tw-max-w-[780px] tw-w-full tw-h-[72px] tw-pl-4 tw-pr-5 tw-py-4 tw-bg-white tw-rounded-lg tw-shadow tw-justify-between tw-items-center tw-inline-flex tw-mb-4 tw-gap-4"
          >
            <div className="tw-justify-start tw-items-center tw-gap-[13px] tw-flex">
              <div className="tw-w-10 tw-h-10 tw-relative">
                <div className="tw-w-10 tw-h-10 tw-left-0 tw-top-0 tw-absolute tw-bg-blue-100 tw-rounded-lg">
                  <div className="tw-w-6 tw-h-6 tw-left-[8px] tw-top-[8px] tw-absolute">{selectFileIcon(file)}</div>
                </div>
              </div>
              <div className="md:tw-w-[299px] tw-flex-col tw-tw-justify-start tw-items-start tw-inline-flex">
                <div
                  title={(file as any)?.original_name}
                  className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                  style={{ wordBreak: 'break-word' }}
                >
                  <span className="md:tw-block tw-hidden">{getFileName(file, 20)}</span>

                  <span className="md:tw-hidden tw-block">{getFileName(file, 12)}</span>
                </div>
                <div className="tw-self-stretch tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {moment((file as File).lastModified || (file as CdnFileType).upload_date_time).format('D MMM, YYYY - h:mm A')}
                </div>
              </div>
            </div>

            {!isUploadInProgress && (
              <PSButton
                id="btn-seeder-remove"
                variant="danger"
                size="sm"
                onClick={() => !removeFileKbAiKittyBotResults.isLoading && handleRemoveSelectedFile(file)}
                disabled={removeFileKbAiKittyBotResults.isLoading}
                isLoading={(file as CdnFileType) === removeFileKbAiKittyBotResults.originalArgs?.data.config.file && removeFileKbAiKittyBotResults.isLoading}
                icon={<BiSolidTrash size={25} className="tw-text-white" />}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(KnowledgeSeederEdit);
